import React from 'react';
import FormFieldWrapper from '@plone/volto/components/manage/Widgets/FormFieldWrapper';
import Image from '@plone/volto/components/theme/Image/Image';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';

export const DEFAULT_FOCAL_POINT = '50% 30%';

const parse = (value) => {
  const match = /^(\d{1,3})% (\d{1,3})%$/.exec(value || '');
  return match ? [Number(match[1]), Number(match[2])] : null;
};

const clamp = (n) => Math.min(100, Math.max(0, Math.round(n)));

// The image being edited: saved (download URL) or just uploaded (base64)
const imageSource = (image) => {
  if (!image) return null;
  if (image.download) return flattenToAppURL(image.download);
  if (image.data && image.encoding === 'base64') {
    return `data:${image['content-type'] || 'image/jpeg'};base64,${image.data}`;
  }
  return null;
};

const PREVIEWS = [
  { name: 'Wide', ratio: '16 / 9' },
  { name: 'Square', ratio: '1 / 1' },
  { name: 'Tall', ratio: '3 / 4' },
];

// Click the part of the image to keep in view; previews show the crops.
// Arrow keys move the point for keyboard users.
const FocalPointWidget = (props) => {
  const { id, value, onChange, formData } = props;
  const src = imageSource(formData?.image);
  const point = parse(value);
  const position = value && point ? value : DEFAULT_FOCAL_POINT;
  const [x, y] = point || parse(DEFAULT_FOCAL_POINT);

  const set = (nx, ny) => onChange(id, `${clamp(nx)}% ${clamp(ny)}%`);

  const onClick = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    set(
      ((event.clientX - rect.left) / rect.width) * 100,
      ((event.clientY - rect.top) / rect.height) * 100,
    );
  };

  const onKeyDown = (event) => {
    const step = event.shiftKey ? 10 : 2;
    const moves = {
      ArrowLeft: [-step, 0],
      ArrowRight: [step, 0],
      ArrowUp: [0, -step],
      ArrowDown: [0, step],
    };
    const move = moves[event.key];
    if (move) {
      event.preventDefault();
      set(x + move[0], y + move[1]);
    }
  };

  return (
    <FormFieldWrapper {...props} className="asaj-focal-field">
      {src ? (
        <div className="asaj-focal">
          <div
            className="asaj-focal-image"
            role="slider"
            tabIndex={0}
            aria-label="Focal point. Click the image, or use the arrow keys."
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={x}
            aria-valuetext={`${x}% across, ${y}% down`}
            onClick={onClick}
            onKeyDown={onKeyDown}
          >
            <Image src={src} alt="" draggable={false} />
            <span
              className="asaj-focal-marker"
              style={{ left: `${x}%`, top: `${y}%` }}
              aria-hidden="true"
            />
          </div>
          <div className="asaj-focal-previews" aria-hidden="true">
            {PREVIEWS.map((preview) => (
              <figure key={preview.name}>
                <Image
                  src={src}
                  alt=""
                  style={{
                    aspectRatio: preview.ratio,
                    objectPosition: position,
                  }}
                />
                <figcaption>{preview.name}</figcaption>
              </figure>
            ))}
          </div>
          {value && (
            <button
              type="button"
              className="asaj-focal-reset"
              onClick={() => onChange(id, null)}
            >
              Reset to the upper middle
            </button>
          )}
        </div>
      ) : (
        <p className="asaj-focal-empty">
          Add an image and save first; then click it here to choose the focal
          point.
        </p>
      )}
    </FormFieldWrapper>
  );
};

export default FocalPointWidget;
